import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../../contexts/ProfileContext';
import {
  createCustomGame,
  generateCustomGameAiClue,
  uploadCustomGameImage,
} from '../../../utils/customGameApi';
import {
  CUSTOM_GAME_AI_CLUE_COST,
  CUSTOM_GAME_CREATE_COST,
  CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW,
  CUSTOM_HUB_VIEW_CREATE,
  CUSTOM_HUB_VIEW_MANAGE,
  CUSTOM_DEFAULT_ZOOM_ORIGIN,
  CUSTOM_DEFAULT_ZOOM_RADIUS,
  CUSTOM_GRID_WORD_LENGTH,
  CUSTOM_MAX_GUESSES_OPTIONS,
  CUSTOM_MIN_FULL_ZOOM_OUT_ROW,
  CUSTOM_STYLE_GRID_5,
  CUSTOM_STYLE_OPEN_ENDED,
  CUSTOM_UNLIMITED_CLUE_REVEAL_CAP,
  CUSTOM_WORD_MAX_LENGTH,
  CUSTOM_WORD_MIN_LENGTH,
  getCustomZoomLevelForRadius,
  isCustomWordLengthValid,
} from '../../../constants/games/zoomedInCustomGame';
import CustomZoomRadiusHelpText from './CustomZoomRadiusHelpText';
import CustomShareLinkCard from './CustomShareLinkCard';
import CustomManageGamesCard from './CustomManageGamesCard';
import CustomGameHubCard from './CustomGameHubCard';
import CustomZoomOriginPicker from './CustomZoomOriginPicker';
import './ZoomedInCustomCreatePage.css';

const sanitizeWordInput = (value) =>
  String(value || '')
    .replace(/[^a-zA-Z]/g, '')
    .toLowerCase();

const ZoomedInCustomCreatePage = () => {
  const navigate = useNavigate();
  const { user, coins, refreshCoins } = useProfile();
  const [style, setStyle] = useState(CUSTOM_STYLE_OPEN_ENDED);
  const [word, setWord] = useState('');
  const [maxGuesses, setMaxGuesses] = useState(5);
  const [isOpenEndedUnlimited, setIsOpenEndedUnlimited] = useState(true);
  const [clueRevealRow, setClueRevealRow] = useState(3);
  const [fullZoomOutRow, setFullZoomOutRow] = useState(CUSTOM_DEFAULT_FULL_ZOOM_OUT_ROW);
  const [clueText, setClueText] = useState('');
  const [zoomOrigin, setZoomOrigin] = useState({
    x: CUSTOM_DEFAULT_ZOOM_ORIGIN.x,
    y: CUSTOM_DEFAULT_ZOOM_ORIGIN.y,
  });
  const [zoomRadius, setZoomRadius] = useState(CUSTOM_DEFAULT_ZOOM_RADIUS);
  const [imageFile, setImageFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isGeneratingAiClue, setIsGeneratingAiClue] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [lastAiModel, setLastAiModel] = useState('');
  const [hasGeneratedAi, setHasGeneratedAi] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [manageRefreshToken, setManageRefreshToken] = useState(0);
  const [activeView, setActiveView] = useState(CUSTOM_HUB_VIEW_CREATE);

  const normalizedWord = sanitizeWordInput(word);
  const wordLength = normalizedWord.length;
  const resolvedMaxGuesses =
    style === CUSTOM_STYLE_OPEN_ENDED && isOpenEndedUnlimited ? null : Number(maxGuesses);
  const clueRevealUpperBound = resolvedMaxGuesses ?? CUSTOM_UNLIMITED_CLUE_REVEAL_CAP;
  const fullZoomOutUpperBound = Math.max(CUSTOM_MIN_FULL_ZOOM_OUT_ROW, clueRevealUpperBound);
  const revealRowOptions = useMemo(
    () => Array.from({ length: Number(clueRevealUpperBound) || 1 }, (_, index) => index + 1),
    [clueRevealUpperBound],
  );
  const fullZoomOutRowOptions = useMemo(
    () =>
      Array.from(
        { length: Number(fullZoomOutUpperBound) - CUSTOM_MIN_FULL_ZOOM_OUT_ROW + 1 },
        (_, index) => index + CUSTOM_MIN_FULL_ZOOM_OUT_ROW,
      ),
    [fullZoomOutUpperBound],
  );

  const canAffordCreate = Number(coins) >= CUSTOM_GAME_CREATE_COST;
  const canAffordAi = Number(coins) >= CUSTOM_GAME_AI_CLUE_COST;

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile]);

  useEffect(() => {
    if (clueRevealRow > clueRevealUpperBound) {
      setClueRevealRow(clueRevealUpperBound);
    }
  }, [clueRevealRow, clueRevealUpperBound]);

  useEffect(() => {
    if (fullZoomOutRow > fullZoomOutUpperBound) {
      setFullZoomOutRow(fullZoomOutUpperBound);
      return;
    }
    if (fullZoomOutRow < CUSTOM_MIN_FULL_ZOOM_OUT_ROW) {
      setFullZoomOutRow(CUSTOM_MIN_FULL_ZOOM_OUT_ROW);
    }
  }, [fullZoomOutRow, fullZoomOutUpperBound]);

  const styleHelpText =
    style === CUSTOM_STYLE_GRID_5
      ? 'Grid 5 style requires exactly 5 letters.'
      : `Open-ended style allows ${CUSTOM_WORD_MIN_LENGTH}-${CUSTOM_WORD_MAX_LENGTH} letters.`;

  const validateForm = () => {
    if (!user) return 'Sign in to create a custom game.';
    if (!imageFile) return 'Please upload an image.';
    if (!normalizedWord) return 'Please enter a secret word.';
    if (!isCustomWordLengthValid({ style, wordLength })) {
      if (style === CUSTOM_STYLE_GRID_5) return 'Grid 5 style requires a 5-letter word.';
      return `Open-ended style requires ${CUSTOM_WORD_MIN_LENGTH}-${CUSTOM_WORD_MAX_LENGTH} letters.`;
    }
    if (
      (style === CUSTOM_STYLE_GRID_5 || !isOpenEndedUnlimited) &&
      !CUSTOM_MAX_GUESSES_OPTIONS.includes(Number(maxGuesses))
    ) {
      return 'Max guesses must be 5 or 6.';
    }
    if (Number(clueRevealRow) < 1 || Number(clueRevealRow) > Number(clueRevealUpperBound)) {
      return 'Clue reveal row is out of range.';
    }
    if (
      Number(fullZoomOutRow) < CUSTOM_MIN_FULL_ZOOM_OUT_ROW ||
      Number(fullZoomOutRow) > Number(fullZoomOutUpperBound)
    ) {
      return `Full zoom-out row must be between ${CUSTOM_MIN_FULL_ZOOM_OUT_ROW} and ${fullZoomOutUpperBound}.`;
    }
    if (!Number.isFinite(Number(zoomRadius)) || Number(zoomRadius) <= 0 || Number(zoomRadius) > 1) {
      return 'Zoom radius must be between 0.01 and 1.';
    }
    if (!canAffordCreate) return `Not enough coins to create (need ${CUSTOM_GAME_CREATE_COST}).`;
    return '';
  };

  const handleCreate = async () => {
    setError('');
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsCreating(true);
    try {
      const uploaded = await uploadCustomGameImage({ file: imageFile });
      const payload = await createCustomGame({
        word: normalizedWord,
        style,
        maxGuesses: resolvedMaxGuesses,
        clueRevealRow: Number(clueRevealRow),
        clueText: String(clueText || '').trim(),
        imageUrl: uploaded.imageUrl,
        imageKey: uploaded.imageKey,
        zoomOriginX: zoomOrigin.x,
        zoomOriginY: zoomOrigin.y,
        zoomRadius: Number(zoomRadius),
        fullZoomOutRow: Number(fullZoomOutRow),
      });
      setCreated(payload);
      setHasGeneratedAi(false);
      setLastAiModel('');
      setManageRefreshToken((previous) => previous + 1);
      await refreshCoins();
    } catch (apiError) {
      setError(apiError?.message || 'Failed to create custom game.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateAiClue = async () => {
    if (!created?.gameId) {
      setError('Create the game first, then generate an AI clue.');
      return;
    }
    if (!canAffordAi) {
      setError(`Not enough coins for AI clue (${CUSTOM_GAME_AI_CLUE_COST}).`);
      return;
    }

    setError('');
    setIsGeneratingAiClue(true);
    try {
      const response = await generateCustomGameAiClue({
        gameId: created.gameId,
        regenerate: hasGeneratedAi,
      });
      setClueText(response.clueText || '');
      setLastAiModel(response.model || '');
      setHasGeneratedAi(true);
      await refreshCoins();
    } catch (apiError) {
      setError(apiError?.message || 'Failed to generate AI clue.');
    } finally {
      setIsGeneratingAiClue(false);
    }
  };

  return (
    <div className="game custom-create-page">
      <CustomGameHubCard
        activeView={activeView}
        onViewChange={(nextView) =>
          setActiveView(nextView === CUSTOM_HUB_VIEW_MANAGE ? CUSTOM_HUB_VIEW_MANAGE : CUSTOM_HUB_VIEW_CREATE)
        }
      />

      {activeView === CUSTOM_HUB_VIEW_CREATE ? (
        <div className="custom-create-page__card">
        <h2>Zoomed In Custom Creator</h2>
        <p className="custom-create-page__subtitle">
          Create costs <strong>{CUSTOM_GAME_CREATE_COST}</strong> coins. AI clue generation costs{' '}
          <strong>{CUSTOM_GAME_AI_CLUE_COST}</strong> coins per click.
        </p>
        <p className="custom-create-page__hint custom-create-page__hint--intro">
          Open-ended defaults to unlimited guesses, but you can set an optional 5/6 limit.
        </p>

        {!user ? (
          <div className="custom-create-page__auth-gate">
            <p>You need an account to create custom games.</p>
            <button type="button" onClick={() => navigate('/auth')}>
              Go to Account
            </button>
          </div>
        ) : null}

        <label className="custom-create-page__field">
          <span>Image Upload</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const nextFile = event.target.files?.[0] || null;
              setImageFile(nextFile);
            }}
          />
        </label>
        {imagePreviewUrl ? (
          <img src={imagePreviewUrl} alt="Upload preview" className="custom-create-page__preview" />
        ) : null}
        <div className="custom-create-page__zoom-controls">
          <span>Custom Zoom Start Area</span>
          <CustomZoomOriginPicker
            imageUrl={imagePreviewUrl}
            value={zoomOrigin}
            zoomLevel={getCustomZoomLevelForRadius(zoomRadius)}
            onChange={(next) => {
              setZoomOrigin({
                x: Math.min(1, Math.max(0, Number(next?.x) || 0.5)),
                y: Math.min(1, Math.max(0, Number(next?.y) || 0.5)),
              });
            }}
          />
          <label className="custom-create-page__field custom-create-page__radius-field">
            <span>Zoom Radius</span>
            <input
              type="number"
              min="0.01"
              max="1"
              step="0.01"
              value={zoomRadius}
              onChange={(event) => setZoomRadius(Number(event.target.value))}
            />
          </label>
          <CustomZoomRadiusHelpText />
        </div>

        <label className="custom-create-page__field">
          <span>Secret Word</span>
          <input
            type="text"
            value={word}
            onChange={(event) => setWord(sanitizeWordInput(event.target.value))}
            placeholder="Enter answer word"
            maxLength={
              style === CUSTOM_STYLE_GRID_5 ? CUSTOM_GRID_WORD_LENGTH : CUSTOM_WORD_MAX_LENGTH
            }
          />
        </label>
        <p className="custom-create-page__hint">
          Length: {wordLength || 0}. {styleHelpText}
        </p>

        <label className="custom-create-page__field">
          <span>Style</span>
          <select
            value={style}
            onChange={(event) => {
              const nextStyle = event.target.value;
              setStyle(nextStyle);
              if (nextStyle === CUSTOM_STYLE_GRID_5) {
                setWord((previous) => sanitizeWordInput(previous).slice(0, CUSTOM_GRID_WORD_LENGTH));
              }
            }}
          >
            <option value={CUSTOM_STYLE_GRID_5}>grid_5 (Wordle-style)</option>
            <option value={CUSTOM_STYLE_OPEN_ENDED}>open_ended (3-12 letters)</option>
          </select>
        </label>

        {style === CUSTOM_STYLE_GRID_5 ? (
          <label className="custom-create-page__field">
            <span>Max Guesses</span>
            <select
              value={maxGuesses}
              onChange={(event) => {
                const next = Number(event.target.value);
                setMaxGuesses(next);
                if (clueRevealRow > next) setClueRevealRow(next);
              }}
            >
              {CUSTOM_MAX_GUESSES_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="custom-create-page__field">
            <span>Open-Ended Guess Mode</span>
            <div className="custom-create-page__guess-mode" role="radiogroup" aria-label="Open-ended guess mode">
              <label
                className={`custom-create-page__guess-mode-option ${
                  isOpenEndedUnlimited ? 'custom-create-page__guess-mode-option--selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="open-ended-guess-mode"
                  checked={isOpenEndedUnlimited}
                  onChange={() => setIsOpenEndedUnlimited(true)}
                />
                <span className="custom-create-page__guess-mode-option-label">Unlimited (default)</span>
              </label>
              <label
                className={`custom-create-page__guess-mode-option ${
                  !isOpenEndedUnlimited ? 'custom-create-page__guess-mode-option--selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="open-ended-guess-mode"
                  checked={!isOpenEndedUnlimited}
                  onChange={() => {
                    setIsOpenEndedUnlimited(false);
                    if (!CUSTOM_MAX_GUESSES_OPTIONS.includes(Number(maxGuesses))) {
                      setMaxGuesses(5);
                    }
                  }}
                />
                <span className="custom-create-page__guess-mode-option-label">
                  Limit to 5 or 6 guesses
                </span>
              </label>
            </div>
            {!isOpenEndedUnlimited ? (
              <select
                className="custom-create-page__guess-mode-limit"
                value={maxGuesses}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setMaxGuesses(next);
                  if (clueRevealRow > next) setClueRevealRow(next);
                }}
              >
                {CUSTOM_MAX_GUESSES_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        )}

        <label className="custom-create-page__field">
          <span>Clue Reveal Row</span>
          <select
            value={clueRevealRow}
            onChange={(event) => setClueRevealRow(Number(event.target.value))}
          >
            {revealRowOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <p className="custom-create-page__hint">
          {resolvedMaxGuesses
            ? `Clue row must be between 1 and ${resolvedMaxGuesses}.`
            : `Unlimited mode clue row cap is 1-${CUSTOM_UNLIMITED_CLUE_REVEAL_CAP}.`}
        </p>

        <label className="custom-create-page__field">
          <span>Full Zoom-Out Row</span>
          <select
            value={fullZoomOutRow}
            onChange={(event) => setFullZoomOutRow(Number(event.target.value))}
          >
            {fullZoomOutRowOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <p className="custom-create-page__hint">
          Image becomes fully zoomed out on this row (between {CUSTOM_MIN_FULL_ZOOM_OUT_ROW} and{' '}
          {fullZoomOutUpperBound}).
        </p>

        <label className="custom-create-page__field">
          <span>Clue Text (Optional)</span>
          <textarea
            value={clueText}
            onChange={(event) => setClueText(event.target.value)}
            rows={4}
            placeholder="Write your own clue, or generate one with AI after create."
          />
        </label>

        <div className="custom-create-page__actions">
          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating || !user || !canAffordCreate}
          >
            {isCreating
              ? 'Creating...'
              : !canAffordCreate
              ? 'Not Enough Coins'
              : `Create Game • ${CUSTOM_GAME_CREATE_COST} coins`}
          </button>

          <button
            type="button"
            onClick={handleGenerateAiClue}
            disabled={isGeneratingAiClue || !created?.gameId || !canAffordAi}
          >
            {isGeneratingAiClue
              ? 'Generating...'
              : hasGeneratedAi
              ? `Regenerate AI Clue • ${CUSTOM_GAME_AI_CLUE_COST} coins`
              : `Generate AI Clue • ${CUSTOM_GAME_AI_CLUE_COST} coins`}
          </button>
        </div>

        {error ? <div className="custom-create-page__error">{error}</div> : null}

        {lastAiModel ? (
          <div className="custom-create-page__hint">AI model: {lastAiModel}</div>
        ) : null}

        {created?.slug ? (
          <>
            <CustomShareLinkCard shareUrl={created.shareUrl} />
            <div className="custom-create-page__post-actions">
              <button type="button" onClick={() => navigate(`/custom/${created.slug}`)}>
                Play This Custom Game
              </button>
            </div>
          </>
        ) : null}
        </div>
      ) : (
        <CustomManageGamesCard refreshToken={manageRefreshToken} />
      )}
    </div>
  );
};

export default ZoomedInCustomCreatePage;
